import React, { useState, useReducer, memo, useMemo, useEffect } from 'react';
import { InfoCircleFilled } from '@ant-design/icons';
import { Table, Alert } from 'antd';
import { Modal } from 'antd';
import { Tabs } from 'antd';
import produce from 'immer';
import PropTypes from 'prop-types';
import { TableMainText, TableSecondaryText } from 'styles';

import { useRewardList } from 'api/rewards';
import { FEATURES, SMALL_PAGE_SIZE } from 'appConstants';
import { TableTimestamp, SearchBar, Balance } from 'components';
import { useFeatureFlags } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import { TTableTypes } from 'utils/globalTranslation/TModules';

import { TRuleListPage } from '../i18n/TModules';

interface RewardSelectModalState {
  state: any;
  page: any;
  size: any;
  order: any;
  sort_by: any;
  search_string: any;
}

const paramsReducer = (state: RewardSelectModalState, action): RewardSelectModalState =>
  produce(state, draft => {
    switch (action.type) {
      case 'STATE':
        draft.state = action.value === 'all' ? null : action.value;
        draft.page = 1;
        break;
      case 'TABLE':
        draft.page = action.pagination;
        if (action.sorter) {
          draft['sort_by'] = action.sorter.columnKey;
          if (action.sorter.order) {
            draft.order = action.sorter.order === 'ascend' ? 'asc' : 'desc';
          }
        }
        break;
      case 'PAGINATION':
        draft.page = action.current;
        if (action.size) {
          draft.size = action.size;
        }

        break;
      case 'SEARCH':
        draft['search_string'] = action.value;
        draft.page = 1;
        break;
      default:
        break;
    }
  });

const RewardSelectModal = ({
  showSystemRewards = false,
  isOutcome = false,
  isOpen = false,
  closeModal,
  setChoice,
  queryParams = {},
  chooseMultiple = false,
  selectedRowKeys,
  selectedRows,
  tableProps,
  showWarning = false,
  tabsToShowWarning = [],
  warningText,
  showInfo = false,
  infoText
}) => {
  const [selectedRewardIds, setSelectedRewardIds] = useState([]);
  const [selectedRewards, setSelectedRewards] = useState([]);
  const [currentPageIds, setCurrentPageIds] = useState([]);
  const [params, paramsDispatch] = useReducer(paramsReducer, {
    state: 'active',
    page: 1,
    order: 'asc',
    sort_by: null,
    search_string: '',
    size: SMALL_PAGE_SIZE
  });

  const featureFlags = useFeatureFlags();
  const privateReward = featureFlags[FEATURES.PRIVATE_REWARDS.key];

  const filterQueryParams = useMemo(() => {
    const query = JSON.parse(JSON.stringify(queryParams));
    if (params.state === 'system') delete query.type;
    return query;
  }, [queryParams, params.state]);

  const rewardListParams = useMemo(
    () => ({ ...filterQueryParams, ...params }),
    [filterQueryParams, params]
  );

  const { data: rewards = [], meta, isLoading } = useRewardList(rewardListParams);

  useEffect(() => {
    paramsDispatch({ type: 'STATE', value: 'active' });
    paramsDispatch({ type: 'SEARCH', value: '' });
  }, [isOpen]);

  useEffect(() => {
    const nextKeys = selectedRowKeys || [];
    const nextRows = selectedRows || [];
    setSelectedRewardIds(prev =>
      JSON.stringify(prev) === JSON.stringify(nextKeys) ? prev : nextKeys
    );
    setSelectedRewards(prev =>
      JSON.stringify(prev) === JSON.stringify(nextRows) ? prev : nextRows
    );
  }, [selectedRowKeys, selectedRows]);

  useEffect(() => {
    if (chooseMultiple) {
      const ids = rewards?.map(reward => reward.id) || [];
      setCurrentPageIds(prev => (JSON.stringify(prev) === JSON.stringify(ids) ? prev : ids));
    }
  }, [rewards, chooseMultiple]);

  const onChange = pagination => {
    paramsDispatch({ type: 'PAGINATION', current: pagination.current });
  };
  const onTabChange = tab => {
    paramsDispatch({ type: 'STATE', value: tab });
  };

  const onRowSelectionChange = async (selectedRowKeys: number[], selectedRows) => {
    // pending: https://github.com/jaredpalmer/formik/issues/529
    // formik setFieldValue is asynchronous since it uses setState internally
    if (!chooseMultiple) await setChoice(selectedRowKeys, selectedRows);
    else {
      setSelectedRewardIds(prev => {
        // Remove any rewards from the current page to avoid duplicates
        const result = prev.filter(rewardId => !currentPageIds.includes(rewardId));
        return [...result, ...selectedRowKeys];
      });
      setSelectedRewards(prev => {
        // Remove any rewards from the current page to avoid duplicates
        const result = prev.filter(reward => !currentPageIds.includes(reward.id));
        return [...result, ...selectedRows];
      });
    }
  };

  const { t } = useMultiLanguageTranslation();

  return (
    <Modal
      destroyOnClose
      maskClosable={false}
      width={1200}
      title={t(`${TRuleListPage}.RewardSelectModal.Title`)}
      open={isOpen}
      onOk={chooseMultiple ? () => setChoice(selectedRewardIds, selectedRewards) : closeModal}
      onCancel={closeModal}
      okText={t(`${TRuleListPage}.RewardSelectModal.AddReward`)}
      cancelText={t(`${TRuleListPage}.RewardSelectModal.Cancel`)}
    >
      <SearchBar
        //@ts-ignore
        onSearch={(value: string) => paramsDispatch({ type: 'SEARCH', value })}
        placeholder={t(`${TRuleListPage}.RewardSelectModal.SearchPlaceholder`)}
        style={{ marginBottom: '1rem' }}
        data-testid="reward-select-searchbar"
      />
      {showInfo && (
        <Alert
          message={infoText}
          type="info"
          icon={<InfoCircleFilled />}
          showIcon
          style={{ border: 'none', marginBottom: '10px' }}
          data-testid="reward-select-info"
        />
      )}
      {!isOutcome ? (
        <Tabs
          activeKey={!params.state ? 'all' : params.state}
          onChange={onTabChange}
          tabBarStyle={{ marginBottom: 0 }}
          data-testid="reward-tabs"
          items={[
            { label: t(`${TRuleListPage}.RewardSelectModal.All`), key: 'all' },
            { label: t(`${TRuleListPage}.RewardSelectModal.Active`), key: 'active' },
            { label: t(`${TRuleListPage}.RewardSelectModal.Approved`), key: 'approved' },
            ...(privateReward
              ? [{ label: t(`${TRuleListPage}.RewardSelectModal.Private`), key: 'private' }]
              : []),
            ...(showSystemRewards
              ? [{ label: t(`${TRuleListPage}.RewardSelectModal.System`), key: 'system' }]
              : [])
          ]}
        />
      ) : (
        <Tabs
          activeKey={params.state}
          onChange={onTabChange}
          data-testid="reward-tabs"
          items={[
            { label: t(`${TRuleListPage}.RewardSelectModal.Active`), key: 'active' },
            { label: t(`${TRuleListPage}.RewardSelectModal.Approved`), key: 'approved' },
            { label: t(`${TRuleListPage}.RewardSelectModal.System`), key: 'system' }
          ]}
        />
      )}
      {tabsToShowWarning.includes(params.state) && showWarning && (
        <Alert
          message=""
          description={warningText}
          type="warning"
          showIcon
          style={{ marginBottom: '1rem' }}
        />
      )}
      <Table<any>
        scroll={{ y: 430 }}
        data-testid="select-reward-modal__table"
        rowKey={c => c.id}
        loading={isLoading}
        onRow={(record, _) => ({
          onClick: () => {
            if (!chooseMultiple) {
              setChoice(record);
              closeModal();
            }
          }
        })}
        rowSelection={
          chooseMultiple
            ? {
                ...(selectedRewardIds &&
                  Array.isArray(selectedRewardIds) && { selectedRowKeys: selectedRewardIds }),
                onChange: onRowSelectionChange
              }
            : null
        }
        columns={[
          {
            title: t(`${TRuleListPage}.RewardSelectModal.Reward`),
            key: 'name',
            render: (_, record: any) => (
              <>
                <TableMainText>{record.name}</TableMainText>
                <TableSecondaryText>
                  {t(`${TRuleListPage}.RewardSelectModal.ID`)}: {record.id}
                </TableSecondaryText>
              </>
            )
          },
          {
            title: t(`${TRuleListPage}.RewardSelectModal.Merchant`),
            dataIndex: 'merchant_name',
            key: 'merchant_name',
            width: 300,
            render: (_, record) => (
              <span style={{ wordBreak: 'break-all' }}>{record['merchant_name']}</span>
            )
          },
          {
            title: t(`${TRuleListPage}.RewardSelectModal.Balance`),
            dataIndex: 'balance',
            key: 'balance',
            width: 150,
            render: (_, record) => (
              <Balance
                current={record?.['lifetime_rewards_available']}
                total={record?.['lifetime_max_rewards']}
              />
            )
          },
          {
            title: t(`${TRuleListPage}.RewardSelectModal.SellingFrom`),
            dataIndex: 'begins_at',
            key: 'begins_at',
            width: 150,
            render: (_, record) => <TableTimestamp timestamp={record['begins_at']} />
          },
          {
            title: t(`${TRuleListPage}.RewardSelectModal.SellingTo`),
            dataIndex: 'ends_at',
            key: 'ends_at',
            width: 150,
            sorter: true,
            render: (_, record) => <TableTimestamp timestamp={record['ends_at']} />
          }
        ]}
        dataSource={rewards}
        onChange={onChange}
        showHeader
        pagination={{
          showSizeChanger: true,
          onShowSizeChange: (current, size) =>
            paramsDispatch({ type: 'PAGINATION', current, size }),
          showQuickJumper: true,
          current: params.page,
          position: ['bottomRight'],
          defaultPageSize: SMALL_PAGE_SIZE,
          total: meta?.count || 0,
          locale: {
            items_per_page: t(`${TTableTypes}.PerPage`),
            jump_to: t(`${TTableTypes}.GoTo`),
            page: t(`${TTableTypes}.Page`),
            prev_page: t(`${TTableTypes}.PreviousPage`),
            next_page: t(`${TTableTypes}.NextPage`)
          }
        }}
        locale={{
          emptyText: t(`${TTableTypes}.NoData`)
        }}
        {...tableProps}
      />
    </Modal>
  );
};

RewardSelectModal.propTypes = {
  isOutcome: PropTypes.bool,
  isOpen: PropTypes.bool,
  closeModal: PropTypes.func.isRequired,
  setChoice: PropTypes.func.isRequired,
  queryParams: PropTypes.object,
  chooseMultiple: PropTypes.bool,
  selectedRowKeys: PropTypes.array,
  selectedRows: PropTypes.array,
  tableProps: PropTypes.object,
  warningText: PropTypes.string,
  showWarning: PropTypes.bool,
  tabsToShowWarning: PropTypes.arrayOf(PropTypes.string),
  showInfo: PropTypes.bool,
  infoText: PropTypes.string
};

export default memo(RewardSelectModal);
